import { useEffect, useMemo, useRef } from 'react'

import { AlarmCodeFilter } from '@/components/ui'
import { useAlarmCodes } from '@/hooks'

import { useMapContext } from '../MapContext'
import { getSeverityFromId } from '../utils'

interface AlarmCodeFilterForMapProps {
  isOpen: boolean
  onToggle: () => void
}

export const AlarmCodeFilterForMap = ({ isOpen, onToggle }: AlarmCodeFilterForMapProps) => {
  // 1. Get context and hooks
  const initializedKeyRef = useRef<string | undefined>(undefined)
  const context = useMapContext()
  const { filters, setFilters, filtersResetCount } = context
  const alarmCodesRaw = useAlarmCodes()

  // 2. Transform alarm codes to the format the generic component expects
  const alarmCodes = useMemo(() => {
    return alarmCodesRaw.map((code) => ({
      id: code.id,
      code: code.code.toString(),
      description: code.description,
      severityId: code.severityId,
      severity: getSeverityFromId(code.severityId),
    }))
  }, [alarmCodesRaw])

  // 4. Handle severity changes
  const handleSeverityChange = (newSeverity: typeof filters.severity) => {
    setFilters({ ...filters, severity: newSeverity })
  }

  // 5. Handle alarm code selection changes
  const handleCodeChange = (codeIds: number[]) => {
    setFilters({ ...filters, alarmCodes: new Set(codeIds) })
  }
  useEffect(() => {
    if (alarmCodes.length === 0) return
    const initKey = `${filters.companyId}:${filtersResetCount}`
    if (initializedKeyRef.current === initKey) return
    initializedKeyRef.current = initKey
    const allIds = alarmCodes.map((c) => c.id)
    setFilters((prev) => ({ ...prev, alarmCodes: new Set(allIds), severity: { ...prev.severity, noAlarms: true } }))
  }, [alarmCodes, filters.companyId, filtersResetCount, setFilters])

  // 6. Handle active since change
  const handleActiveSinceChange = (value: string | null) => {
    setFilters({ ...filters, alarmsActiveSince: value })
  }

  // 7. Bridge context to props
  return (
    <AlarmCodeFilter
      alarmCodes={alarmCodes}
      selectedCodeIds={Array.from(filters.alarmCodes)}
      onChange={handleCodeChange}
      severityFilter={filters.severity}
      onSeverityChange={handleSeverityChange}
      showSeverityCheckboxes={true}
      showNoAlarmsCheckbox={true}
      renderMode="dropdown"
      isOpen={isOpen}
      onToggle={onToggle}
      activeSince={filters.alarmsActiveSince}
      onActiveSinceChange={handleActiveSinceChange}
      isLoading={alarmCodesRaw.length === 0}
    />
  )
}
